/**
 * Collection Vehicle UI Manager
 * Handles DOM manipulation and UI operations for vehicle display
 */
class CollectionVehicleUI {
  static CSS_CLASSES = {
    CONTAINER: 'collection-easysearch-custom',
    RESULTS_DISPLAY: 'collection-vehicle-results',
    RESULTS_TEXT: 'collection-vehicle-results__text',
    RESULTS_ACTIONS: 'collection-vehicle-results__actions',
    SEARCH_BUTTON: 'collection-vehicle-results__search',
    CLEAR_BUTTON: 'collection-vehicle-results__clear',
    FORM_CONTAINER: 'collection-vehicle-form',
    TITLE_CONTAINER: 'title-easysearch',
    FORM_WIDGET: 'easysearch-embed-widget-main',
    SECTION_FAQS_CONTENT: 'section-faqs-with-content'
  };
  
  static DISPLAY_MESSAGES = {
    SHOWING_RESULTS: (vehicleText) => `Showing Results Recommended For Your: <strong>${vehicleText}</strong>`,
    SHOWING_GENERIC: 'Showing Results Recommended For Your Vehicle',
    SEARCH_ANOTHER: 'Search For Another Vehicle',
    CLEAR_FILTER: 'CLEAR VEHICLE FILTER'
  };

  static ICONS = {
    EASYSEARCH: '#icon-easysearch'
  };

  constructor() {
    this.eventHandlers = new Map();
  }

  /**
   * Find all collection container elements
   */
  findContainers() {
    return document.querySelectorAll(`.${CollectionVehicleUI.CSS_CLASSES.CONTAINER}`);
  }

  /**
   * Show vehicle results display in container
   */
  showVehicleDisplay(container, vehicleData, callbacks = {}) {
    if (!container) return;

    // Hide default elements
    this.hideDefaultElements(container);

    // Create or update results display
    let resultsDisplay = this.findResultsDisplay(container);
    
    if (!resultsDisplay) {
      resultsDisplay = this.createResultsDisplay(callbacks);
      container.appendChild(resultsDisplay);
    }

    // Update the display content
    this.updateResultsContent(resultsDisplay, vehicleData);
    
    // Show the results display
    this.showElement(resultsDisplay);

    const pageType = window.ShopifyAnalytics?.meta?.page?.pageType;
    const isCollection =
    pageType === 'collection' ||
    /^\/collections\/[^/]+\/?$/.test(window.location.pathname);

    if (isCollection) {
        const el = document.querySelector(`.${CollectionVehicleUI.CSS_CLASSES.SECTION_FAQS_CONTENT}`);
        if (el) el.style.display = 'none';
    }
    
  }

  /**
   * Show default form display in container
   */
  showDefaultDisplay(container) {
    if (!container) return;

    // Show default elements
    this.showDefaultElements(container);

    // Hide results display if it exists
    const resultsDisplay = this.findResultsDisplay(container);
    if (resultsDisplay) {
      this.hideElement(resultsDisplay);
    }
  }

  /**
   * Hide default form elements
   */
  hideDefaultElements(container) {
    const elements = [
      container.querySelector(`.${CollectionVehicleUI.CSS_CLASSES.TITLE_CONTAINER}`),
      container.querySelector(`.${CollectionVehicleUI.CSS_CLASSES.FORM_WIDGET}`)
    ];

    elements.forEach(element => {
      if (element) this.hideElement(element);
    });
  }

  /**
   * Show default form elements
   */
  showDefaultElements(container) {
    const elements = [
      container.querySelector(`.${CollectionVehicleUI.CSS_CLASSES.TITLE_CONTAINER}`),
      container.querySelector(`.${CollectionVehicleUI.CSS_CLASSES.FORM_WIDGET}`)
    ];

    elements.forEach(element => {
      if (element) this.showElement(element);
    });
  }

  /**
   * Find existing results display in container
   */
  findResultsDisplay(container) {
    return container.querySelector(`.${CollectionVehicleUI.CSS_CLASSES.RESULTS_DISPLAY}`);
  }

  /**
   * Create new results display element
   */
  createResultsDisplay(callbacks = {}) {
    const resultsDisplay = document.createElement('div');
    resultsDisplay.className = CollectionVehicleUI.CSS_CLASSES.RESULTS_DISPLAY;
    
    resultsDisplay.innerHTML = this.generateResultsHTML();

    // Attach event listeners
    this.attachEventListeners(resultsDisplay, callbacks);

    return resultsDisplay;
  }

  /**
   * Generate HTML for results display
   */
  generateResultsHTML() {
    return `
      <div class="${CollectionVehicleUI.CSS_CLASSES.RESULTS_TEXT}">
        <svg class="icon icon-easysearch" width="30" height="35" aria-hidden="true" focusable="false">
          <use href="${CollectionVehicleUI.ICONS.EASYSEARCH}"></use>
        </svg>
        <span class="results-message" role="status" aria-live="polite"></span>
      </div>
      <div class="${CollectionVehicleUI.CSS_CLASSES.RESULTS_ACTIONS}">
        <button type="button" 
                class="${CollectionVehicleUI.CSS_CLASSES.SEARCH_BUTTON} link"
                aria-label="Search for a different vehicle">
          ${CollectionVehicleUI.DISPLAY_MESSAGES.SEARCH_ANOTHER}
        </button>
        <button type="button" 
                class="${CollectionVehicleUI.CSS_CLASSES.CLEAR_BUTTON} button button--secondary"
                aria-label="Clear current vehicle filter">
          ${CollectionVehicleUI.DISPLAY_MESSAGES.CLEAR_FILTER}
        </button>
      </div>
    `;
  }

  /**
   * Update results display content with vehicle data
   */
  updateResultsContent(resultsDisplay, vehicleData) {
    const messageElement = resultsDisplay.querySelector('.results-message');
    if (!messageElement) return;

    const displayText = this.getVehicleDisplayText(vehicleData);
    
    if (displayText) {
      messageElement.innerHTML = CollectionVehicleUI.DISPLAY_MESSAGES.SHOWING_RESULTS(displayText);
    } else {
      messageElement.innerHTML = CollectionVehicleUI.DISPLAY_MESSAGES.SHOWING_GENERIC;
    }
  }

  /**
   * Get display text for vehicle
   */
  getVehicleDisplayText(vehicleData) {
    if (!vehicleData) return null;

    if (vehicleData.year && vehicleData.model) {
      const details = vehicleData.details && vehicleData.details !== 'none' ? vehicleData.details : '';
      return `${vehicleData.year} ${vehicleData.model} ${details}`.trim();
    } 
    else if (vehicleData.year || vehicleData.model) {
      return vehicleData.year || vehicleData.model;
    }
    
    return null;
  }

  /**
   * Attach event listeners to results display
   */
  attachEventListeners(resultsDisplay, callbacks) {
    const searchButton = resultsDisplay.querySelector(`.${CollectionVehicleUI.CSS_CLASSES.SEARCH_BUTTON}`);
    const clearButton = resultsDisplay.querySelector(`.${CollectionVehicleUI.CSS_CLASSES.CLEAR_BUTTON}`);

    if (searchButton && callbacks.onSearchAnother) {
      const handler = (e) => {
        e.preventDefault();
        callbacks.onSearchAnother();
      };
      
      searchButton.addEventListener('click', handler);
      this.eventHandlers.set(searchButton, handler);
    }

    if (clearButton && callbacks.onClearFilter) {
      const handler = (e) => {
        e.preventDefault();
        callbacks.onClearFilter();
      };
      
      clearButton.addEventListener('click', handler);
      this.eventHandlers.set(clearButton, handler);
    }
  }

  /**
   * Remove event listeners (cleanup)
   */
  removeEventListeners() {
    this.eventHandlers.forEach((handler, element) => {
      element.removeEventListener('click', handler);
    });
    this.eventHandlers.clear();
  }

  /**
   * Show element with proper display style
   */
  showElement(element) {
    if (element) {
      element.style.display = '';
    }
  }

  /**
   * Hide element
   */
  hideElement(element) {
    if (element) {
      element.style.display = 'none';
    }
  }

  /**
   * Update all containers with current state
   */
  updateAllContainers(vehicleData, callbacks = {}) {
    const containers = this.findContainers();
    
    containers.forEach(container => {
      if (vehicleData && this.hasValidVehicleData(vehicleData)) {
        this.showVehicleDisplay(container, vehicleData, callbacks);
      } else {
        this.showDefaultDisplay(container);
      }
    });
  }

  /**
   * Check if vehicle data is valid for display
   */
  hasValidVehicleData(vehicleData) {
    return vehicleData && 
           vehicleData.metaobjectId && 
           (vehicleData.year || vehicleData.model);
  }


  /**
   * Get debug information about UI state
   */
  getDebugInfo() {
    const containers = this.findContainers();
    
    return {
      containerCount: containers.length,
      activeEventHandlers: this.eventHandlers.size,
      cssClasses: CollectionVehicleUI.CSS_CLASSES,
      displayMessages: CollectionVehicleUI.DISPLAY_MESSAGES,
      stylesInjected: !!document.getElementById('collection-vehicle-ui-styles')
    };
  }

  /**
   * Clean up all UI elements and event listeners
   */
  cleanup() {
    // Remove event listeners
    this.removeEventListeners();
    
    // Remove all results displays
    const containers = this.findContainers();
    containers.forEach(container => {
      const resultsDisplay = this.findResultsDisplay(container);
      if (resultsDisplay) {
        resultsDisplay.remove();
      }
      
      // Restore default elements
      this.showDefaultDisplay(container);
    });
    
    console.log('🗑️ Collection vehicle UI cleaned up');
  }
}

// Export for use in other modules
if (typeof window !== 'undefined') {
  window.CollectionVehicleUI = CollectionVehicleUI;
}